import React, {ReactElement} from "react"
import {Application, ApplicationStatus, EvaluationOutcome, getApplicationName} from "../../resources/application"
import {Resource} from "../../resources/common"
import {AllowedApplicationsColumns, ApplicationsColumns} from "./Application.types"
import {useNavigateWithListPath} from "../../hooks/useListReturnPath"
import Icon from "../Icon/Icon"
import classNames from "classnames"
import {DataTableCell, DataTableRow} from "../DataTable/DataTable"
import {OrgName} from "../OrgName/OrgName"
import Tag, {TagTheme} from "../Tag/Tag"
import {startCase} from "lodash"
import moment from "moment"
import IdLink from "../IdLink/IdLink"

interface ApplicationRowProps {
	application: Application
	include?: Resource[]
	includedColumns: Array<AllowedApplicationsColumns>
}

export default function ApplicationRow({application, include, includedColumns}: ApplicationRowProps) {
	const navigateWithListPath = useNavigateWithListPath()

	const id = application.id
	const org = application.relationships.org?.data.id
	const status = application.attributes.status
	const createdAt = application.attributes.createdAt
	const updatedAt = application.attributes.updatedAt
	const name = getApplicationName(application)
	let evaluation = ""
	let iconType: JSX.Element = <></>

	switch (application.type) {
		case "businessApplication":
			evaluation = application.attributes.evaluationOutcome
			iconType = <Icon icon={"toolbox"} />
			break
		case "individualApplication":
			evaluation = application.attributes.evaluationOutcome
			iconType = <Icon icon={"user-geometric-action---users"} />
			break
	}

	const contentColumns: Partial<Record<AllowedApplicationsColumns, ReactElement>> = {}

	if (includedColumns.includes(ApplicationsColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell className={classNames("data-table-id-cell")}>
				<IdLink href={`/applications/${id}`}>{id}</IdLink>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(ApplicationsColumns.type)) {
		contentColumns["Type"] = <DataTableCell>{iconType}</DataTableCell>
	}

	if (includedColumns.includes(ApplicationsColumns.org)) {
		contentColumns["Org"] = <OrgName orgId={org.toString()} included={include} element={DataTableCell} />
	}

	if (includedColumns.includes(ApplicationsColumns.name)) {
		contentColumns["Name"] = <DataTableCell>{name}</DataTableCell>
	}

	if (includedColumns.includes(ApplicationsColumns.status)) {
		const statusRecord: Record<ApplicationStatus, TagTheme> = {
			[ApplicationStatus.AwaitingDocuments]: "neutral",
			[ApplicationStatus.AwaitingInformation]: "neutral",
			[ApplicationStatus.PendingReview]: "warning",
			[ApplicationStatus.Approved]: "success",
			[ApplicationStatus.Denied]: "error",
			[ApplicationStatus.Pending]: "warning",
			[ApplicationStatus.Canceled]: "error",
		}

		contentColumns["Status"] = (
			<DataTableCell>
				<Tag size="large" variant="outline" theme={statusRecord[status]} content={startCase(status)} />
			</DataTableCell>
		)
	}

	if (includedColumns.includes(ApplicationsColumns.evaluation)) {
		const evaluationRecord: Record<EvaluationOutcome, TagTheme> = {
			[EvaluationOutcome.Approved]: "success",
			[EvaluationOutcome.Denied]: "error",
			[EvaluationOutcome.Pending]: "warning",
			[EvaluationOutcome.PendingReview]: "warning",
		}

		contentColumns["Evaluation"] = (
			<DataTableCell>
				<Tag
					size="large"
					variant="outline"
					theme={evaluationRecord[evaluation as EvaluationOutcome]}
					content={startCase(evaluation)}
				/>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(ApplicationsColumns.createdAt)) {
		contentColumns["Created At"] = <DataTableCell>{moment(createdAt).format("L LT")}</DataTableCell>
	}

	if (includedColumns.includes(ApplicationsColumns.updatedAt)) {
		contentColumns["Updated At"] = (
			<DataTableCell>
				{updatedAt ? moment(updatedAt).format("L LT") : <span className="empty-date-placeholder"> --/--/---- </span>}
			</DataTableCell>
		)
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col, i) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})
	return (
		<DataTableRow
			onClick={(e) => {
				e.preventDefault()
				navigateWithListPath(`/applications/${id}`)
			}}
		>
			{content}
		</DataTableRow>
	)
}
